import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Camera, Upload, X, Copy, Download,
  Loader2, Check, AlertCircle, Eye, EyeOff, Plus, Globe, Phone,
  Instagram, Twitter, Facebook, Trash2, Image as ImageIcon
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { 
  fetchMyProfile, 
  upsertMyProfile, 
  checkSlugAvailability, 
  fetchProfileQrCode, 
  getProfileUploadUrl, 
  uploadImageToR2, 
  addCustomDomain,
  removeCustomDomain,
  ProfileData, 
  ServiceItem 
} from '@/api/profiles';
import { mediaUpload } from '@/services/mediaUpload';

const CATEGORIES = [
  'Fashion', 'Sneakers', 'Electronics', 'Food & Drinks', 'Beauty',
  'Furniture', 'Health', 'Services', 'Art', 'Books', 'Sports', 'Other',
];

const FieldLabel = ({ label, required = false, isMissing = false }: { label: string, required?: boolean, isMissing?: boolean }) => (
  <label className="flex items-center text-sm font-semibold text-light-text dark:text-gray-200 mb-2">
    {label}
    {required && <span className="text-danger ml-1">*</span>}
    {isMissing && <span className="w-2 h-2 rounded-full bg-danger ml-2" title="Required information missing"></span>}
  </label>
);

const EditBusinessProfilePage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const toastCtx = useToast();
  
  const showToast = (opts: { type: 'success' | 'error' | 'warning' | 'info'; message: string }) => {
    if (opts.type === 'success') toastCtx.success(opts.message);
    else if (opts.type === 'error') toastCtx.error('Error', opts.message);
    else if (opts.type === 'warning') toastCtx.warning('Warning', opts.message);
    else if (opts.type === 'info') toastCtx.info('Info', opts.message);
  };

  const [businessName, setBusinessName] = useState('');
  const [businessLogo, setBusinessLogo] = useState<string | null>(null);

  const [profileData, setProfileData] = useState<ProfileData>({
    slug: '',
    tagline: '',
    description: '',
    gallery_images: [],
    services: [],
    template_id: 'classic',
    is_published: true,
    custom_domain: null,
    custom_domain_status: null,
  });

  const [customDomainInput, setCustomDomainInput] = useState('');
  const [connectingDomain, setConnectingDomain] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [serviceImgUploading, setServiceImgUploading] = useState<number | null>(null);

  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'ok' | 'taken' | 'short'>('idle');
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedSlugRef = useRef<string>('');

  useEffect(() => {
    if (!businessId) return;
    loadBusinessProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const loadBusinessProfile = async () => {
    try {
      setLoading(true);
      const res = await fetchMyProfile(businessId!);
      
      setBusinessName(res.business.name || '');
      setBusinessLogo(res.business.logo_url || null);

      if (res.profile) {
        const p = res.profile;
        setProfileData({
          slug: p.slug || '',
          tagline: p.tagline || '',
          description: res.business.description || '',
          cover_image_url: p.cover_image_url || undefined,
          logo_url: p.logo_url || undefined,
          gallery_images: p.gallery_images || [],
          services: p.services || [],
          website_url: p.website_url || '',
          whatsapp_number: p.whatsapp_number || '',
          instagram_url: p.instagram_url || '',
          twitter_url: p.twitter_url || '',
          facebook_url: p.facebook_url || '',
          template_id: p.template_id || 'classic',
          is_published: p.is_published !== false,
          custom_domain: p.custom_domain || null,
          custom_domain_status: p.custom_domain_status || null,
        });
        savedSlugRef.current = p.slug || '';
        setSlugStatus('ok');
        
        loadQrCode();
      }
    } catch (err: any) {
      console.error('Failed to load profile:', err);
      showToast({ type: 'error', message: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const loadQrCode = async () => {
    try {
      setQrLoading(true);
      const qrRes = await fetchProfileQrCode(businessId!);
      setQrCode(qrRes.qr_base64);
      setPublicUrl(qrRes.public_url);
    } catch (err) {
      console.error('Failed to load QR code:', err);
    } finally {
      setQrLoading(false);
    }
  };

  const handleSlugChange = (value: string) => {
    const clean = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/--+/g, '-');
    setProfileData(prev => ({ ...prev, slug: clean }));

    if (clean.length < 3) {
      setSlugStatus('short');
      return;
    }

    if (clean === savedSlugRef.current) {
      setSlugStatus('ok');
      return;
    }

    setSlugStatus('checking');
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const result = await checkSlugAvailability(businessId!, clean);
        setSlugStatus(result.available ? 'ok' : 'taken');
      } catch {
        // If there is an error, we reset to 'idle' so the user can try saving
        setSlugStatus('idle');
      }
    }, 400); // reduced from 600 to 400 for snappier validation
  };

  const uploadToR2Helper = async (file: File, type: "cover" | "gallery" | "service"): Promise<string | null> => {
    try {
      const url = await mediaUpload.uploadImageWithToast(file, `profile-media/${businessId}/${type === 'service' ? 'store' : type}`);
      if (!url) {
        showToast({ type: 'error', message: `Failed to upload ${type}` });
      }
      return url;
    } catch (err) {
      console.error(err);
      showToast({ type: 'error', message: `Failed to upload ${type}` });
      return null;
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;
    setCoverUploading(true);
    const url = await uploadToR2Helper(file, 'cover');
    if (url) {
      setProfileData(prev => ({ ...prev, cover_image_url: url }));
      showToast({ type: 'success', message: 'Cover image uploaded' });
    }
    setCoverUploading(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;
    setLogoUploading(true);
    const url = await uploadToR2Helper(file, 'cover'); // Profiles treat logo same as cover storage
    if (url) {
      setProfileData(prev => ({ ...prev, logo_url: url }));
      showToast({ type: 'success', message: 'Logo uploaded' });
    }
    setLogoUploading(false);
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !businessId) return;

    setGalleryUploading(true);
    for (const file of files) {
      const url = await uploadToR2Helper(file, 'gallery');
      if (url) {
        setProfileData(prev => ({
          ...prev,
          gallery_images: [...(prev.gallery_images || []), url].slice(0, 12),
        }));
      }
    }
    setGalleryUploading(false);
  };

  const handleRemoveGalleryImage = (index: number) => {
    setProfileData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images?.filter((_, i) => i !== index),
    }));
  };

  const handleServiceImageUpload = async (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;
    setServiceImgUploading(idx);
    const url = await uploadToR2Helper(file, 'service');
    if (url) {
      setProfileData(prev => ({
        ...prev,
        services: (prev.services || []).map((s, i) => i === idx ? { ...s, image_url: url } : s)
      }));
    }
    setServiceImgUploading(null);
  };

  const addService = () => {
    if ((profileData.services?.length || 0) >= 12) return;
    setProfileData(prev => ({
      ...prev,
      services: [...(prev.services || []), { name: '', description: '', price: '', image_url: null }]
    }));
  };

  const removeService = (idx: number) => {
    setProfileData(prev => ({
      ...prev,
      services: (prev.services || []).filter((_, i) => i !== idx)
    }));
  };

  const updateService = (idx: number, field: keyof ServiceItem, value: any) => {
    setProfileData(prev => ({
      ...prev,
      services: (prev.services || []).map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };

  const handleSave = async () => {
    if (!profileData.slug || profileData.slug.length < 3) {
      showToast({ type: 'error', message: 'Please enter a valid username (min. 3 characters)' });
      return;
    }
    if (slugStatus === 'taken') {
      showToast({ type: 'error', message: 'Username already taken' });
      return;
    }
    if (slugStatus === 'checking') {
      showToast({ type: 'error', message: 'Please wait for username check' });
      return;
    }

    try {
      setSaving(true);
      const res = await upsertMyProfile(businessId!, profileData);
      savedSlugRef.current = res.profile.slug;
      setPublicUrl(res.public_url);
      setSlugStatus('ok');
      await loadQrCode();
      showToast({ type: 'success', message: '✅ Profile saved successfully!' });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.response?.data?.error || err.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    const domain = customDomainInput.trim().toLowerCase();
    if (!domain) {
      showToast({ type: 'error', message: 'Please enter a domain' });
      return;
    }
    if (domain.includes('majengo.co.ke')) {
      showToast({ type: 'error', message: 'You cannot use a majengo.co.ke subdomain here' });
      return;
    }
    
    setConnectingDomain(true);
    try {
      const res = await addCustomDomain(businessId!, domain);
      setProfileData(prev => ({ ...prev, custom_domain: res.domain, custom_domain_status: 'pending' as const }));
      setCustomDomainInput('');
      showToast({ type: 'success', message: 'Domain connection initiated' });
    } catch (err: any) {
      showToast({ type: 'error', message: err.response?.data?.error || 'Failed to connect domain' });
    } finally {
      setConnectingDomain(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm('Are you sure you want to disconnect this domain?')) return;
    try {
      await removeCustomDomain(businessId!);
      setProfileData(prev => ({ ...prev, custom_domain: null, custom_domain_status: null }));
      showToast({ type: 'info', message: 'Custom domain removed' });
    } catch (err: any) {
      showToast({ type: 'error', message: 'Failed to remove domain' });
    }
  };

  const handleCopyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      showToast({ type: 'success', message: 'Link copied!' });
    } catch {
      showToast({ type: 'error', message: 'Failed to copy' });
    }
  };

  const handleDownloadQR = () => {
    if (!qrCode) return;
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `${profileData.slug}-qr.png`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-[#121212]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Artistic depth styles for dark mode: outer bg is very dark #121212, internal cards are #1E1E1E, inputs are #2A2A2A
  // We use tailwind's dark mode classes to achieve this cleanly without borders.
  const cardClass = "rounded-2xl p-6 bg-white dark:bg-[#1E1E1E] shadow-sm";
  const inputClass = "w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl text-light-text dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary transition-colors";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212] pb-12">
      {/* Custom Header (Matches Main Portal Size) */}
      <div className="sticky top-0 z-40 h-16 bg-white dark:bg-[#1E1E1E] shadow-sm flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-[#2A2A2A] hover:opacity-80 transition"
          >
            <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-200" />
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">Majengo Profile</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-primary hover:opacity-90 disabled:opacity-50 text-white rounded-full font-semibold transition"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* ── Hero Identity Section ── */}
            <div className="rounded-3xl bg-white dark:bg-[#1E1E1E] overflow-hidden shadow-sm">
              <label className="block relative group cursor-pointer h-52 sm:h-64 bg-gray-200 dark:bg-[#2A2A2A]">
                {profileData.cover_image_url ? (
                  <img
                    src={profileData.cover_image_url}
                    alt="Cover"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center relative z-10">
                    <div className="w-14 h-14 rounded-full bg-black/20 backdrop-blur-md flex items-center justify-center mb-3">
                      <Camera className="h-6 w-6 text-white" />
                    </div>
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Add Cover Photo</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20">
                  <span className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-md text-white font-semibold rounded-full shadow-lg">
                    <Upload size={18} /> Change Cover
                  </span>
                </div>
                {coverUploading && (
                  <div className="absolute inset-0 bg-black/60 z-30 flex items-center justify-center backdrop-blur-sm">
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
              </label>

              <div className="px-6 pb-8 relative">
                <div className="flex flex-col sm:flex-row sm:items-end gap-6 -mt-16 mb-2 relative z-40">
                  <label className="relative cursor-pointer group flex-shrink-0">
                    <div className="h-32 w-32 rounded-3xl bg-white dark:bg-[#1E1E1E] p-1.5 shadow-lg">
                      <div className="w-full h-full rounded-2xl bg-gradient-to-br from-primary to-[#0EA5E9] flex items-center justify-center text-white font-black text-4xl overflow-hidden relative">
                        {logoUploading ? (
                          <Loader2 className="h-8 w-8 animate-spin" />
                        ) : profileData.logo_url ? (
                          <img src={profileData.logo_url} alt="Logo" className="w-full h-full object-cover" />
                        ) : businessLogo ? (
                          <img src={businessLogo} alt="Business Logo" className="w-full h-full object-cover" />
                        ) : (
                          businessName.charAt(0).toUpperCase()
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Camera className="h-8 w-8 text-white" />
                        </div>
                      </div>
                    </div>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>

                  <div className="flex-1 pb-2">
                    <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
                      {businessName}
                      {!profileData.logo_url && <span className="w-2.5 h-2.5 rounded-full bg-danger" title="Logo missing"></span>}
                    </h2>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary" />
                      Business Identity
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Form */}
            <div className="space-y-6">
              
              {/* Username/Slug */}
              <div className={cardClass}>
                <FieldLabel label="Majengo Username" required isMissing={!profileData.slug || slugStatus !== 'ok'} />
                <div className="flex items-center gap-3 mb-2 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl px-4 overflow-hidden focus-within:ring-2 focus-within:ring-primary transition-shadow">
                  <input
                    type="text"
                    value={profileData.slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="your-business"
                    className="flex-1 py-3 bg-transparent text-gray-900 dark:text-white focus:outline-none font-semibold text-right"
                  />
                  <span className="text-gray-500 dark:text-gray-400 font-medium">.majengo.co.ke</span>
                  {slugStatus === 'checking' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                  {slugStatus === 'ok' && <Check className="h-5 w-5 text-success" />}
                  {(slugStatus === 'taken' || slugStatus === 'short') && (
                    <AlertCircle className="h-5 w-5 text-danger" />
                  )}
                </div>
                {slugStatus === 'short' && <p className="text-xs text-danger font-medium mt-2">Minimum 3 characters</p>}
                {slugStatus === 'taken' && <p className="text-xs text-danger font-medium mt-2">Username already taken</p>}
              </div>

              {/* Tagline */}
              <div className={cardClass}>
                <FieldLabel label="Tagline" isMissing={!profileData.tagline} />
                <input
                  type="text"
                  value={profileData.tagline || ''}
                  onChange={(e) => setProfileData(prev => ({ ...prev, tagline: e.target.value }))}
                  placeholder="e.g., Your trusted business partner"
                  maxLength={80}
                  className={inputClass}
                />
              </div>

              {/* Description */}
              <div className={cardClass}>
                <FieldLabel label="Description" isMissing={!profileData.description} />
                <textarea
                  value={profileData.description || ''}
                  onChange={(e) => setProfileData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Tell customers about your business..."
                  maxLength={500}
                  rows={4}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Services & Products */}
              <div className={cardClass}>
                <div className="flex items-center justify-between mb-6">
                  <FieldLabel label="Services & Products" isMissing={!profileData.services?.length} />
                  <button 
                    onClick={addService}
                    className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 font-semibold rounded-lg transition"
                  >
                    <Plus className="h-4 w-4" /> Add
                  </button>
                </div>
                
                {(!profileData.services || profileData.services.length === 0) && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No services added yet. Add your offerings to attract customers.
                  </div>
                )}

                <div className="space-y-4">
                  {profileData.services?.map((svc, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-[#2A2A2A] rounded-2xl overflow-hidden flex flex-col sm:flex-row">
                      <label className="sm:w-48 h-48 sm:h-auto bg-gray-200 dark:bg-[#333333] relative cursor-pointer group flex-shrink-0">
                        {serviceImgUploading === idx ? (
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                          </div>
                        ) : svc.image_url ? (
                          <img src={svc.image_url} alt={svc.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 group-hover:text-primary transition-colors">
                            <ImageIcon className="h-8 w-8 mb-2" />
                            <span className="text-xs font-semibold">Add Photo</span>
                          </div>
                        )}
                        <input type="file" accept="image/*" onChange={(e) => handleServiceImageUpload(idx, e)} className="hidden" />
                      </label>
                      <div className="p-5 flex-1 flex flex-col gap-3">
                        <div className="flex items-start justify-between">
                          <input 
                            type="text" 
                            placeholder="Service Name *" 
                            value={svc.name} 
                            onChange={(e) => updateService(idx, 'name', e.target.value)}
                            className="bg-transparent text-lg font-bold text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none w-full"
                          />
                          <button onClick={() => removeService(idx)} className="text-danger hover:bg-danger/10 p-2 rounded-lg transition shrink-0 ml-2">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <textarea 
                          placeholder="Short description (optional)" 
                          value={svc.description || ''} 
                          onChange={(e) => updateService(idx, 'description', e.target.value)}
                          rows={2}
                          className="bg-transparent text-sm text-gray-600 dark:text-gray-300 placeholder-gray-500 focus:outline-none resize-none w-full"
                        />
                        <div className="flex gap-2">
                          <input 
                            type="text" 
                            placeholder="Price (KES 500)" 
                            value={svc.price || ''} 
                            onChange={(e) => updateService(idx, 'price', e.target.value)}
                            className="bg-white dark:bg-[#1E1E1E] px-3 py-2 rounded-lg text-sm font-semibold text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none flex-1"
                          />
                          <input 
                            type="text" 
                            placeholder="Stock Qty" 
                            value={svc.quantity_in_stock || ''} 
                            onChange={(e) => updateService(idx, 'quantity_in_stock', e.target.value)}
                            className="bg-white dark:bg-[#1E1E1E] px-3 py-2 rounded-lg text-sm font-semibold text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none flex-1"
                          />
                        </div>
                        <input 
                          type="text" 
                          placeholder="Sizes (e.g. S, M, L or 40, 41)" 
                          value={svc.sizes || ''} 
                          onChange={(e) => updateService(idx, 'sizes', e.target.value)}
                          className="bg-white dark:bg-[#1E1E1E] px-3 py-2 rounded-lg text-sm font-semibold text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none w-full"
                        />
                        <div className="flex items-center gap-2 overflow-x-auto pb-1 hide-scrollbar">
                          {CATEGORIES.map(cat => (
                            <button
                              key={cat}
                              onClick={() => updateService(idx, 'category', svc.category === cat ? '' : cat)}
                              className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-semibold border transition ${
                                svc.category === cat 
                                  ? 'bg-primary border-primary text-white' 
                                  : 'bg-white dark:bg-[#1E1E1E] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                              }`}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                        <div className="mt-auto pt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!svc.sold_out}
                              onChange={(e) => updateService(idx, 'sold_out', e.target.checked)}
                              className="w-4 h-4 text-danger rounded focus:ring-danger"
                            />
                            <span className={`text-sm font-semibold ${svc.sold_out ? 'text-danger' : 'text-gray-500 dark:text-gray-400'}`}>
                              Mark as Sold Out
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gallery */}
              <div className={cardClass}>
                <div className="flex items-center justify-between mb-4">
                  <FieldLabel label={`Gallery (${profileData.gallery_images?.length || 0}/12)`} isMissing={!profileData.gallery_images?.length} />
                  <label className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary text-sm font-semibold rounded-lg cursor-pointer transition">
                    <Plus className="h-4 w-4" /> Add
                    <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />
                  </label>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                  {profileData.gallery_images?.map((img, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden aspect-square bg-gray-100 dark:bg-[#2A2A2A]">
                      <img src={img} alt={`Gallery ${idx}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleRemoveGalleryImage(idx)}
                        className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      >
                        <X className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  ))}
                  {(profileData.gallery_images?.length || 0) < 12 && (
                    <label className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center justify-center aspect-square cursor-pointer hover:border-primary dark:hover:border-primary transition bg-gray-50 dark:bg-[#2A2A2A]">
                      {galleryUploading ? (
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Upload className="h-6 w-6 text-gray-400 mb-2" />
                          <span className="text-xs font-semibold text-gray-500">Upload</span>
                        </>
                      )}
                      <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />
                    </label>
                  )}
                </div>
              </div>

              {/* Social Links */}
              <div className={cardClass}>
                <FieldLabel label="Contact & Socials" isMissing={!profileData.website_url && !profileData.whatsapp_number && !profileData.instagram_url} />
                <div className="space-y-4">
                  {[
                    { key: 'website_url', icon: Globe, placeholder: 'Website URL' },
                    { key: 'whatsapp_number', icon: Phone, placeholder: 'WhatsApp (+254...)' },
                    { key: 'instagram_url', icon: Instagram, placeholder: 'Instagram profile URL' },
                    { key: 'twitter_url', icon: Twitter, placeholder: 'Twitter/X URL' },
                    { key: 'facebook_url', icon: Facebook, placeholder: 'Facebook page URL' },
                  ].map(({ key, icon: Icon, placeholder }) => (
                    <div key={key} className="flex items-center gap-3 bg-gray-50 dark:bg-[#2A2A2A] px-4 py-1 rounded-xl">
                      <Icon className="h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={(profileData as any)[key] || ''}
                        onChange={(e) => setProfileData(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="flex-1 py-3 bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6 lg:sticky lg:top-24 h-fit">
            
            {/* Publish Toggle */}
            <div className={cardClass}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Profile Visibility</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {profileData.is_published ? 'Public & Discoverable' : 'Hidden'}
                  </p>
                </div>
                <button
                  onClick={() => setProfileData(prev => ({ ...prev, is_published: !prev.is_published }))}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none ${
                    profileData.is_published ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                      profileData.is_published ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* QR Code Card */}
            <div className={cardClass}>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Share QR Code</h3>
              <div className="flex flex-col items-center">
                {qrLoading ? (
                  <div className="h-48 w-48 flex items-center justify-center bg-gray-50 dark:bg-[#2A2A2A] rounded-2xl">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : qrCode ? (
                  <img src={qrCode} alt="QR Code" className="h-48 w-48 rounded-2xl shadow-sm" />
                ) : (
                  <div className="h-48 w-48 bg-gray-50 dark:bg-[#2A2A2A] rounded-2xl flex flex-col items-center justify-center text-gray-400">
                    <span className="text-sm font-semibold">Save profile first</span>
                  </div>
                )}
              </div>
              {qrCode && (
                <button
                  onClick={handleDownloadQR}
                  className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-[#2A2A2A] hover:bg-gray-200 dark:hover:bg-[#333333] text-gray-900 dark:text-white rounded-xl text-sm font-bold transition"
                >
                  <Download className="h-4 w-4" /> Download QR
                </button>
              )}
            </div>

            {/* Public URL Card */}
            {publicUrl && (
              <div className={cardClass}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Public Link</h3>
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl">
                  <Globe className="h-5 w-5 text-gray-400 shrink-0" />
                  <input
                    type="text"
                    value={publicUrl}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white truncate outline-none"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="p-2 hover:bg-white dark:hover:bg-[#333333] rounded-lg transition shadow-sm"
                  >
                    <Copy className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              </div>
            )}

            {/* Custom Domain Card */}
            <div className={cardClass}>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">Custom Domain</h3>
              
              {profileData.custom_domain ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{profileData.custom_domain}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      profileData.custom_domain_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      profileData.custom_domain_status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {profileData.custom_domain_status || 'pending'}
                    </span>
                  </div>

                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl text-xs text-blue-800 dark:text-blue-200">
                    <p className="font-bold mb-1">Action Required:</p>
                    <p className="mb-2">Go to your domain registrar and add this record:</p>
                    <div className="flex items-center justify-between bg-white dark:bg-[#1E1E1E] p-2 rounded">
                      <code className="font-mono">CNAME &rarr; domains.majengo.co.ke</code>
                      <button onClick={() => { navigator.clipboard.writeText('domains.majengo.co.ke'); showToast({ type: 'success', message: 'Copied!' }); }} className="text-blue-500 hover:text-blue-700"><Copy className="h-3 w-3" /></button>
                    </div>
                  </div>

                  <button
                    onClick={handleRemoveDomain}
                    className="w-full py-2.5 rounded-xl border-2 border-red-200 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition text-sm font-bold"
                  >
                    Disconnect Domain
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your own domain (e.g. shop.com)</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="yourdomain.com"
                      value={customDomainInput}
                      onChange={e => setCustomDomainInput(e.target.value)}
                      className="flex-1 px-4 py-2 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      onClick={handleAddDomain}
                      disabled={connectingDomain || !customDomainInput}
                      className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:opacity-90 transition disabled:opacity-50"
                    >
                      {connectingDomain ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Template Selection */}
            <div className={cardClass}>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Store Template</h3>
              <div className="space-y-3">
                {['classic', 'bold', 'minimal'].map(id => (
                  <button
                    key={id}
                    onClick={() => setProfileData(prev => ({ ...prev, template_id: id }))}
                    className={`w-full px-5 py-3 rounded-xl text-sm font-bold transition flex items-center justify-between ${
                      profileData.template_id === id
                        ? 'bg-primary text-white shadow-md shadow-primary/20'
                        : 'bg-gray-50 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#333333]'
                    }`}
                  >
                    {id.charAt(0).toUpperCase() + id.slice(1)}
                    {profileData.template_id === id && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBusinessProfilePage;
